import { useState, useEffect, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { useTheme } from "../context/ThemeContext";
import { Palette, Check, Eye, Plus, Trash2, Pencil, RotateCcw, Download, Upload, CalendarClock } from "lucide-react";

const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const FIELDS = [
  { key: "base", label: "Page Background" },
  { key: "s1", label: "Card Surface" },
  { key: "s2", label: "Elevated Surface" },
  { key: "s3", label: "Border / Hover" },
  { key: "brandblue", label: "Primary Accent" },
  { key: "pos", label: "Positive / Profit" },
  { key: "neg", label: "Negative / Loss" },
  { key: "gold", label: "Highlight" },
  { key: "fg", label: "Body Text" },
  { key: "muted", label: "Muted Text" },
];

const BLANK = { base: "#0A0B0E", s1: "#12141A", s2: "#1C1F26", s3: "#262A33", brandblue: "#3B82F6", pos: "#10B981", neg: "#EF4444", gold: "#F59E0B", fg: "#F8FAFC", muted: "#97A3B6" };

function ThemeSwatch({ p }) {
  return (
    <div className="rounded-lg overflow-hidden border border-white/10 shadow-inner" style={{ background: p.base }}>
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="h-2 w-16 rounded-full" style={{ background: p.fg, opacity: 0.9 }} />
          <div className="h-4 w-4 rounded-full" style={{ background: p.brandblue }} />
        </div>
        <div className="rounded-md p-2 space-y-1.5" style={{ background: p.s1, border: `1px solid ${p.s3}` }}>
          <div className="h-1.5 w-20 rounded-full" style={{ background: p.muted }} />
          <div className="flex gap-1.5">
            <div className="h-5 flex-1 rounded" style={{ background: p.s2 }} />
            <div className="h-5 w-8 rounded" style={{ background: p.brandblue }} />
          </div>
          <div className="flex gap-1.5">
            <span className="h-2 flex-1 rounded-full" style={{ background: p.pos }} />
            <span className="h-2 flex-1 rounded-full" style={{ background: p.neg }} />
            <span className="h-2 w-4 rounded-full" style={{ background: p.gold }} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminAppearance() {
  const [builtin, setBuiltin] = useState([]);
  const [custom, setCustom] = useState([]);
  const [activeId, setActiveId] = useState("midnight");
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState(null); // {name, description, id, palette}
  const [schedules, setSchedules] = useState([]);
  const [schedForm, setSchedForm] = useState({ name: "", theme_id: "", start: "", end: "" });
  const { applyPalette, resetTheme, commitPalette, previewing } = useTheme();
  const fileRef = useRef(null);

  const allThemes = [...builtin, ...custom];
  const themeName = (id) => allThemes.find((t) => t.id === id)?.name || id;
  const schedStatus = (s) => {
    const now = Date.now(), st = new Date(s.start).getTime(), en = new Date(s.end).getTime();
    if (now < st) return { label: "Upcoming", cls: "text-brandblue bg-brandblue/15" };
    if (now > en) return { label: "Expired", cls: "text-fx-s3 bg-white/5" };
    return { label: "Active now", cls: "text-pos bg-pos/15" };
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/themes");
      setBuiltin(data.builtin || []);
      setCustom(data.custom || []);
      setActiveId(data.active_id);
    } catch { toast.error("Failed to load themes"); }
    try {
      const { data } = await api.get("/admin/theme-schedules");
      setSchedules(data || []);
    } catch { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const preview = (t) => { applyPalette(t.palette); toast.info(`Previewing "${t.name}" — activate to keep it`); };

  const activate = async (t) => {
    setBusy(true);
    try {
      await api.put("/admin/themes/active", { id: t.id });
      commitPalette(t.palette);
      setActiveId(t.id);
      toast.success(`"${t.name}" is now the active theme`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const openNew = () => setEditor({ name: "", description: "", id: "", palette: { ...BLANK } });
  const openEdit = (t) => setEditor({ name: t.name, description: t.description || "", id: t.id, palette: { ...t.palette } });

  const setColor = (key, val) => {
    setEditor((prev) => {
      const next = { ...prev, palette: { ...prev.palette, [key]: val } };
      applyPalette(next.palette); // live preview while editing
      return next;
    });
  };

  const closeEditor = () => { setEditor(null); resetTheme(); };

  const saveTheme = async (activateAfter) => {
    if (!editor.name.trim()) { toast.error("Give your theme a name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/themes", { id: editor.id || "", name: editor.name.trim(), description: editor.description, palette: editor.palette });
      toast.success("Theme saved");
      setEditor(null);
      if (activateAfter) { await api.put("/admin/themes/active", { id: data.id }); commitPalette(data.palette); toast.success(`"${data.name}" activated`); }
      else resetTheme();
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save"); }
    finally { setBusy(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete custom theme "${t.name}"?`)) return;
    try {
      const { data } = await api.delete(`/admin/themes/${t.id}`);
      toast.success("Theme deleted");
      if (t.id === activeId && data.active_id) setActiveId(data.active_id);
      await load();
    } catch { toast.error("Failed to delete"); }
  };

  const exportTheme = (t) => {
    const payload = { _fxtheme: 1, name: t.name, description: t.description || "", palette: t.palette };
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }));
    a.download = `fx-theme-${t.id}.json`;
    a.click();
    toast.success(`Exported "${t.name}"`);
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      const palette = parsed.palette || {};
      const bad = FIELDS.filter((f) => !HEX.test(String(palette[f.key] || "")));
      if (!parsed.name || bad.length) { toast.error("Invalid theme file — missing name or colors"); return; }
      const clean = {}; FIELDS.forEach((f) => { clean[f.key] = palette[f.key]; });
      const { data } = await api.post("/admin/themes", { name: parsed.name, description: parsed.description || "", palette: clean });
      toast.success(`Imported "${data.name}"`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not import theme file"); }
  };

  const createSchedule = async () => {
    if (!schedForm.theme_id) { toast.error("Pick a theme"); return; }
    if (!schedForm.start || !schedForm.end) { toast.error("Set a start and end date"); return; }
    try {
      await api.post("/admin/theme-schedules", schedForm);
      toast.success("Theme scheduled");
      setSchedForm({ name: "", theme_id: "", start: "", end: "" });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to schedule"); }
  };

  const delSchedule = async (id) => {
    try { await api.delete(`/admin/theme-schedules/${id}`); toast.success("Schedule removed"); await load(); }
    catch { toast.error("Failed to remove"); }
  };

  const Card = ({ t, isCustom }) => {
    const active = t.id === activeId;
    return (
      <div data-testid={`theme-card-${t.id}`} className={`rounded-xl border p-4 space-y-3 transition-all ${active ? "border-brandblue ring-1 ring-brandblue/40 bg-brandblue/5" : "border-white/10 bg-fx-s1 hover:border-white/20"}`}>
        <ThemeSwatch p={t.palette} />
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-sm">{t.name}</h4>
              {active && <span className="text-[10px] font-bold uppercase tracking-wider text-pos bg-pos/15 px-1.5 py-0.5 rounded flex items-center gap-1"><Check className="h-3 w-3" />Active</span>}
            </div>
            <p className="text-xs text-fx-s3 mt-0.5">{t.description}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" data-testid={`theme-preview-${t.id}`} onClick={() => preview(t)} className="h-8 border-white/10"><Eye className="h-3.5 w-3.5 mr-1" />Preview</Button>
          <Button size="sm" data-testid={`theme-activate-${t.id}`} disabled={active || busy} onClick={() => activate(t)} className="h-8 bg-brandblue hover:bg-brandblue/90">{active ? "Active" : "Activate"}</Button>
          <Button size="sm" variant="outline" data-testid={`theme-export-${t.id}`} onClick={() => exportTheme(t)} title="Export as JSON" className="h-8 border-white/10"><Download className="h-3.5 w-3.5" /></Button>
          {isCustom && <>
            <Button size="sm" variant="outline" data-testid={`theme-edit-${t.id}`} onClick={() => openEdit(t)} className="h-8 border-white/10"><Pencil className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="outline" data-testid={`theme-delete-${t.id}`} onClick={() => del(t)} className="h-8 border-neg/40 text-neg hover:bg-neg/10"><Trash2 className="h-3.5 w-3.5" /></Button>
          </>}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-appearance">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-brandblue" />
          <div>
            <h3 className="font-display text-xl font-bold">Theme Manager</h3>
            <p className="text-xs text-fx-s3">Change the platform's look across every page. Changes apply instantly for all users.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="application/json,.json" onChange={onImportFile} className="hidden" data-testid="theme-import-input" />
          <Button variant="outline" data-testid="theme-import-btn" onClick={() => fileRef.current?.click()} className="border-white/10"><Upload className="h-4 w-4 mr-1" />Import</Button>
          <Button data-testid="theme-create-btn" onClick={openNew} className="bg-brandblue hover:bg-brandblue/90"><Plus className="h-4 w-4 mr-1" />Create Custom Theme</Button>
        </div>
      </div>

      {previewing && (
        <div data-testid="theme-preview-bar" className="flex items-center justify-between gap-3 rounded-lg border border-gold/40 bg-gold/10 px-4 py-2.5">
          <p className="text-sm text-gold font-medium flex items-center gap-2"><Eye className="h-4 w-4" />You're previewing an unsaved theme. Activate it to make it permanent.</p>
          <Button size="sm" variant="outline" data-testid="theme-reset-preview" onClick={resetTheme} className="h-8 border-gold/40 text-gold hover:bg-gold/10"><RotateCcw className="h-3.5 w-3.5 mr-1" />Reset</Button>
        </div>
      )}

      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-fx-s3 mb-3">Built-in Themes</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {builtin.map((t) => <Card key={t.id} t={t} isCustom={false} />)}
        </div>
      </div>

      {custom.length > 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-fx-s3 mb-3">Custom Themes</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {custom.map((t) => <Card key={t.id} t={t} isCustom={true} />)}
          </div>
        </div>
      )}

      {/* Scheduled Themes */}
      <div data-testid="theme-schedules" className="rounded-xl border border-white/10 bg-fx-s1 p-4 space-y-4">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-brandblue" />
          <div>
            <h4 className="font-semibold text-sm">Scheduled Themes</h4>
            <p className="text-xs text-fx-s3">Auto-switch to a seasonal theme during a date range — it reverts to your active theme automatically when the window ends.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
          <div className="space-y-1">
            <Label className="text-[11px]">Label</Label>
            <Input data-testid="sched-name" value={schedForm.name} onChange={(e) => setSchedForm({ ...schedForm, name: e.target.value })} placeholder="e.g. Holiday" className="h-9 bg-fx-base border-white/10" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Theme</Label>
            <select data-testid="sched-theme" value={schedForm.theme_id} onChange={(e) => setSchedForm({ ...schedForm, theme_id: e.target.value })} className="h-9 w-full rounded-md bg-fx-base border border-white/10 text-sm px-2 text-white">
              <option value="">Select…</option>
              {allThemes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Start (UTC)</Label>
            <Input data-testid="sched-start" type="datetime-local" value={schedForm.start} onChange={(e) => setSchedForm({ ...schedForm, start: e.target.value })} className="h-9 bg-fx-base border-white/10" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">End (UTC)</Label>
            <Input data-testid="sched-end" type="datetime-local" value={schedForm.end} onChange={(e) => setSchedForm({ ...schedForm, end: e.target.value })} className="h-9 bg-fx-base border-white/10" />
          </div>
          <Button data-testid="sched-add-btn" onClick={createSchedule} className="h-9 bg-brandblue hover:bg-brandblue/90"><Plus className="h-4 w-4 mr-1" />Schedule</Button>
        </div>

        {schedules.length === 0 ? (
          <p className="text-xs text-fx-s3 text-center py-2">No scheduled themes yet.</p>
        ) : (
          <div className="space-y-2">
            {schedules.map((s) => {
              const st = schedStatus(s);
              return (
                <div key={s.id} data-testid={`sched-row-${s.id}`} className="flex items-center justify-between gap-3 rounded-lg bg-fx-base border border-white/5 px-3 py-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{s.name}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${st.cls}`}>{st.label}</span>
                    </div>
                    <div className="text-xs text-fx-s3 font-mono truncate">{themeName(s.theme_id)} · {new Date(s.start).toLocaleString()} → {new Date(s.end).toLocaleString()}</div>
                  </div>
                  <Button size="sm" variant="outline" data-testid={`sched-delete-${s.id}`} onClick={() => delSchedule(s.id)} className="h-8 border-neg/40 text-neg hover:bg-neg/10 shrink-0"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!editor} onOpenChange={(o) => { if (!o) closeEditor(); }}>
        <DialogContent className="bg-fx-s1 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editor?.id ? "Edit" : "Create"} Custom Theme</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Theme Name</Label><Input data-testid="theme-name-input" value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} placeholder="e.g. Sunset Trader" className="bg-fx-base border-white/10" /></div>
                <div className="space-y-1"><Label className="text-xs">Description</Label><Input value={editor.description} onChange={(e) => setEditor({ ...editor, description: e.target.value })} placeholder="Short description" className="bg-fx-base border-white/10" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {FIELDS.map((f) => (
                  <div key={f.key} className="flex items-center gap-2 rounded-lg border border-white/10 bg-fx-base px-2.5 py-2">
                    <input type="color" data-testid={`theme-color-${f.key}`} value={editor.palette[f.key]} onChange={(e) => setColor(f.key, e.target.value)} className="h-8 w-8 rounded cursor-pointer bg-transparent border-0 p-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-medium truncate">{f.label}</p>
                      <input value={editor.palette[f.key]} onChange={(e) => setColor(f.key, e.target.value)} className="w-full bg-transparent text-xs font-mono text-fx-s3 outline-none uppercase" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="rounded-lg border border-white/10 p-1"><ThemeSwatch p={editor.palette} /></div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeEditor} className="border-white/10">Cancel</Button>
            <Button variant="outline" data-testid="theme-save-btn" disabled={busy} onClick={() => saveTheme(false)} className="border-white/10">Save</Button>
            <Button data-testid="theme-save-activate-btn" disabled={busy} onClick={() => saveTheme(true)} className="bg-brandblue hover:bg-brandblue/90">Save & Activate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
